import { useEffect, useState, type JSX } from 'react';

import { css } from '@compiled/react';
import { UniformSlot } from '@uniformdev/canvas-react';
import { ManifestErrorMessage } from 'src/uniform/utils/manifest-error-message';

import { experiments } from '@/utils/experiment/manifest';
import { Experiment, useExperiment } from '@/utils/experiment/use-experiment';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { isProduction } from '@/utils/urls';

import ExperimentOverrideControls, { type ExperimentVariant } from './experiment-override-controls';
import UniformExperimentWithoutManifest from './experiment-without-manifest';

export interface UniformExperimentProps {
	experimentId: string;
	postExperimentationMode?: boolean;
	override?: ExperimentVariant;
	bypassManifest?: boolean;
}

type ManualOverrideState = ExperimentVariant | null;
type ExperimentStatusShape = { type: string; value?: unknown };

const WRAPPER_STYLES = css({
	position: 'relative',
});

const renderSlot = (variant: ExperimentVariant): JSX.Element => <UniformSlot name={variant} />;

const getSelectedVariantFromExperimentStatus = (
	experimentStatus: ExperimentStatusShape,
): ExperimentVariant =>
	experimentStatus.type === 'enrolled' && experimentStatus.value ? 'treatment' : 'control';

const getActiveVariant = ({
	manualOverride,
	propOverride,
	experimentStatus,
}: {
	manualOverride: ManualOverrideState;
	propOverride?: ExperimentVariant;
	experimentStatus?: ExperimentStatusShape;
}): ExperimentVariant => {
	if (manualOverride) {
		return manualOverride;
	}

	if (propOverride) {
		return propOverride;
	}

	if (experimentStatus) {
		return getSelectedVariantFromExperimentStatus(experimentStatus);
	}

	return 'control';
};

const UniformExperiment = (props: UniformExperimentProps) => {
	const { experimentId, override, postExperimentationMode, bypassManifest = false } = props;

	if (bypassManifest) {
		return (
			<UniformExperimentWithoutManifest
				experimentId={experimentId}
				override={override}
				postExperimentationMode={postExperimentationMode}
			/>
		);
	}

	return <UniformExperimentWithManifest {...props} />;
};

const UniformExperimentWithManifest = (props: UniformExperimentProps) => {
	const { experimentId, override, postExperimentationMode } = props;

	const isProd = isProduction();

	const experiment = Object.entries(experiments).find(
		([, value]) => value.experimentName === experimentId,
	)?.[1];

	// eslint-disable-next-line react-hooks/rules-of-hooks -- intentional: experiment is stable per render, this is safe
	const experimentStatus = experiment
		? // oxlint-disable-next-line react-hooks/rules-of-hooks
			(useExperiment(experiment as Experiment<unknown>) as ExperimentStatusShape)
		: undefined;

	const [manualOverride, setManualOverride] = useState<ManualOverrideState>(null);

	useEffect(() => {
		setManualOverride(null);
	}, [experimentId]);

	if (postExperimentationMode) {
		return (
			<FeatureExposed experimentKey={experimentId} group="Test">
				<UniformSlot name="treatment" />
			</FeatureExposed>
		);
	}

	if (!isProd) {
		const activeVariant = getActiveVariant({
			manualOverride,
			propOverride: override,
			experimentStatus,
		});

		return (
			<div css={WRAPPER_STYLES}>
				<ExperimentOverrideControls
					experimentId={experimentId}
					selectedVariant={manualOverride}
					setSelectedVariant={setManualOverride}
				/>
				{!experiment && <ManifestErrorMessage id={experimentId} />}
				{renderSlot(activeVariant)}
			</div>
		);
	}

	if (!experiment || !experimentStatus) {
		return renderSlot('control');
	}

	return renderSlot(getSelectedVariantFromExperimentStatus(experimentStatus));
};

export default UniformExperiment;
